import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

const UserAssistantComponent = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [directives, setDirectives] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { fetchDirectives(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchDirectives = async () => {
    try {
      const res = await axios.get(`${API}/javier/directives`);
      setDirectives(res.data.directives || []);
    } catch (err) { console.error(err); }
  };

  const dismissDirective = async (id) => {
    try {
      await axios.post(`${API}/javier/directives/${id}/dismiss`);
      setDirectives(prev => prev.filter(d => d.id !== id));
    } catch (err) { console.error(err); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg, timestamp: new Date().toISOString() }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/javier/user-chat`, {
        message: userMsg,
        session_id: sessionId
      });
      if (!sessionId) setSessionId(res.data.session_id);
      setMessages(prev => [...prev, {
        role: 'assistant', content: res.data.response, timestamp: res.data.timestamp
      }]);
    } catch (err) {
      const errMsg = err.response?.data?.detail || 'Error de comunicacion con el Asistente';
      setMessages(prev => [...prev, { role: 'error', content: errMsg, timestamp: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { label: 'Mi balance', msg: 'Como esta mi balance actual? Cuanto tengo disponible?' },
    { label: 'Mis tareas', msg: 'Que tareas tengo pendientes y cuanto puedo ganar?' },
    { label: 'Mis ventas', msg: 'Como van mis ventas en el marketplace?' },
    { label: 'Consejos', msg: 'Dame consejos para aprovechar mejor CUALAPP' },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="user-assistant">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"/>
            </svg>
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm" data-testid="assistant-title">Asistente CUAL</h3>
            <p className="text-emerald-100 text-xs">Tu asistente inteligente personal</p>
          </div>
          {sessionId && (
            <button onClick={() => { setMessages([]); setSessionId(null); }} data-testid="assistant-new-session"
              className="ml-auto text-xs bg-white/15 text-white px-2.5 py-1 rounded-lg hover:bg-white/25 transition-colors">
              Nueva sesion
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="h-80 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {/* Active Directives */}
        {directives.length > 0 && (
          <div className="space-y-2 mb-3" data-testid="directives-container">
            {directives.map((d) => (
              <div key={d.id} data-testid={`directive-${d.id}`}
                className="bg-amber-50 border border-amber-300 rounded-lg p-3 relative">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-amber-700 mb-0.5">Aviso del Administrador</p>
                    <p className="text-sm text-amber-900">{d.message}</p>
                    <p className="text-[10px] text-amber-500 mt-1">
                      {d.created_at ? new Date(d.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                  </div>
                  <button onClick={() => dismissDirective(d.id)} data-testid={`dismiss-directive-${d.id}`}
                    className="text-amber-400 hover:text-amber-600 text-xs flex-shrink-0">
                    x
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <svg className="w-10 h-10 text-emerald-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
            </svg>
            <p className="text-emerald-600 text-sm font-medium">Hola! Soy tu Asistente CUAL</p>
            <p className="text-gray-500 text-xs mt-1">Preguntame sobre tu cuenta, balance, tareas o lo que necesites</p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {quickActions.map((cmd, i) => (
                <button key={i} onClick={() => { setInput(cmd.msg); inputRef.current?.focus(); }}
                  data-testid={`user-quick-${i}`}
                  className="px-3 py-1.5 text-xs bg-white text-emerald-700 rounded-lg border border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all">
                  {cmd.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            data-testid={`assistant-msg-${idx}`}>
            <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 ${
              msg.role === 'user'
                ? 'bg-emerald-600 text-white rounded-br-sm'
                : msg.role === 'error'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm shadow-sm'
            }`}>
              {msg.role === 'assistant' && (
                <p className="text-[10px] text-emerald-600 font-medium mb-1">Asistente CUAL</p>
              )}
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</div>
              {msg.timestamp && (
                <p className={`text-[10px] mt-1 ${msg.role === 'user' ? 'text-emerald-200' : 'text-gray-400'}`}>
                  {new Date(msg.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-xl px-3.5 py-2.5 shadow-sm">
              <p className="text-[10px] text-emerald-600 font-medium mb-1">Asistente CUAL</p>
              <div className="flex items-center space-x-1.5">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay:'0ms'}}></span>
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay:'150ms'}}></span>
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay:'300ms'}}></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="border-t border-gray-200 p-3 bg-white">
        <div className="flex items-center gap-2">
          <input ref={inputRef} type="text" value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Pregunta algo..." disabled={loading}
            data-testid="assistant-input"
            className="flex-1 bg-gray-50 text-gray-800 placeholder-gray-400 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 disabled:opacity-50" />
          <button type="submit" disabled={loading || !input.trim()} data-testid="assistant-send-btn"
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 disabled:opacity-30 transition-colors">
            {loading ? '...' : 'Enviar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserAssistantComponent;
